import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { useOfflineSync } from '@core/hooks/useOfflineSync';
import { Button } from '@components/ui/Button';
import { cn } from '@lib/utils';

interface OfflineIndicatorProps {
  /**
   * Posición del indicador
   */
  position?: 'top' | 'bottom';
  
  /**
   * Si se muestra en modo compacto
   */
  compact?: boolean;
}

/**
 * Indicador de estado de conexión y sincronización
 * 
 * Muestra el estado online/offline y operaciones pendientes
 */
export function OfflineIndicator({ position = 'top', compact = false }: OfflineIndicatorProps) {
  const { isOnline, pendingCount, isSyncing, syncError, syncNow } = useOfflineSync();

  // No mostrar nada si está online y no hay operaciones pendientes
  if (isOnline && pendingCount === 0 && !syncError) {
    return null;
  }

  const positionClasses = {
    top: 'top-0 rounded-b-lg',
    bottom: 'bottom-0 rounded-t-lg',
  };

  return (
    <div
      className={cn(
        'fixed left-1/2 z-50 -translate-x-1/2 animate-in slide-in-from-top shadow-lg',
        'max-w-[calc(100vw-2rem)]', // Prevenir overflow horizontal
        positionClasses[position]
      )}
    >
      {/* Banner offline */}
      {!isOnline && (
        <div className="flex items-center gap-3 bg-danger-500 px-4 py-2 text-white">
          <WifiOff className="h-4 w-4" />
          {!compact && (
            <>
              <span className="text-sm font-medium">Sin conexión</span>
              {pendingCount > 0 && (
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {pendingCount} pendientes
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Banner sincronizando */}
      {isOnline && isSyncing && pendingCount > 0 && (
        <div className="flex items-center gap-3 bg-warning-500 px-4 py-2 text-white">
          <RefreshCw className="h-4 w-4 animate-spin" />
          {!compact && (
            <span className="text-sm font-medium">
              Sincronizando {pendingCount} operaciones...
            </span>
          )}
        </div>
      )}

      {/* Banner error de sync */}
      {syncError && (
        <div className="flex items-center justify-between gap-3 bg-danger-500 px-4 py-2 text-white">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-4 w-4" />
            {!compact && <span className="text-sm font-medium">{syncError}</span>}
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={syncNow}
            disabled={isSyncing}
          >
            Reintentar
          </Button>
        </div>
      )}

      {/* Banner operaciones pendientes (online pero no syncing) */}
      {isOnline && !isSyncing && pendingCount > 0 && (
        <div className="flex items-center justify-between gap-3 bg-merino-500 px-4 py-2 text-white">
          <div className="flex items-center gap-3">
            <Wifi className="h-4 w-4" />
            {!compact && (
              <span className="text-sm font-medium">
                {pendingCount} operaciones pendientes
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={syncNow}
            isLoading={isSyncing}
          >
            Sincronizar
          </Button>
        </div>
      )}
    </div>
  );
}
